import { ActionButton, Callout, DefaultEffects, DirectionalHint, ITheme, mergeStyles, PrimaryButton, Stack, Text, TooltipHost } from '@fluentui/react';
import { AreaDto } from 'services/client-api-wrapper/campus_api/responses.dto';
import { IC } from 'components/SVGIcon';
import { Steps } from 'intro.js-react';
import 'intro.js/introjs.css';
import { getTextColorForBackground } from 'kits/colorKit';
import { EnterOffice, MoreStrong, PresenceIcon, LevelElevatorPolygon } from 'kits/IconKit2';
import IvicosStrings from 'kits/language/stringKit';
import React, { ReactElement, useState } from 'react';
import { useArea, useAreaGroup, useDeleteAreaOrAreaGroup } from 'shared-state/directory/hooks';
import { IIdentityClaims } from 'shared-state/identity/types';
import { useWindowWidth } from 'shared-state/display-options/hooks';
import { getPresenceTooltip } from 'kits/language/stringHelpers';
import { useNavigate } from 'react-router';
import { showTooltipForTruncatedText, truncateText } from 'kits/truncateTextKit';

export interface IAreaCardViewProps {
    fluid?: boolean;
    panelHeader: string;
    isPanelOpen: boolean;
    dismissPanel: () => void;
    campusId: any;
    areaName: string;
    setAreaName: React.Dispatch<React.SetStateAction<string>>;
    vertical?: boolean;
    title: string;
    label: string;
    description?: string;
    isPublic?: boolean;
    onClick?: (e: any) => void;
    onRenderActions: () => ReactElement;
    setPanelHeader: React.Dispatch<React.SetStateAction<string>>;
    openPanel: () => void;
    areaId: string;
    modalIsOpen: boolean;
    setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
    seedAreaEditPanel: (areaId: string, name?: string, tag?: string, sortNumber?: number) => void;
    theme: ITheme;
    getPrimaryCardColor: () => string;
    stepsEnabled?: boolean;
    setStepsEnabled: React.Dispatch<React.SetStateAction<boolean | undefined>>;
    steps: {
        initialStep: number;
        steps: (
            | {
                  element: string;
                  intro: string;
                  position: string;
                  tooltipClass: string;
                  showProgress?: undefined;
                  showBullets?: undefined;
              }
            | {
                  element: string;
                  intro: string;
                  position: string;
                  showProgress: boolean;
                  showBullets: boolean;
                  tooltipClass?: undefined;
              }
        )[];
    };
    options: {
        doneLabel: string;
        hideNext: boolean;
        showStepNumbers: boolean;
        showBullets: boolean;
        hidePrev: boolean;
    };
    areaCardWidth: string;
    areaCardWidthVertical: string;
    area?: AreaDto;
    descriptionId: string;
    labelId: string;
    buttonId: string;
    identityClaims?: IIdentityClaims;
    isCalloutVisible: boolean;
    toggleIsCalloutVisible: () => void;
    color: string | undefined;
    usersAndVisitorsCount: { users: number; visitors: number };
    groupAreaProps:
        | {
              groupId: string;
              groupLevel: string;
          }
        | undefined;
    setIsAddAreaGroupSelected: React.Dispatch<React.SetStateAction<boolean>>;
}

const AreaCardView: React.FC<IAreaCardViewProps> = (props) => {
    const {
        vertical,
        title,
        description,
        onClick,
        onRenderActions,
        setPanelHeader,
        openPanel,
        areaId,
        modalIsOpen,
        seedAreaEditPanel,
        setModalIsOpen,
        theme,
        getPrimaryCardColor,
        stepsEnabled,
        setStepsEnabled,
        steps,
        options,
        areaCardWidth,
        areaCardWidthVertical,
        descriptionId,
        labelId,
        buttonId,
        identityClaims,
        isCalloutVisible,
        toggleIsCalloutVisible,
        color,
        usersAndVisitorsCount,
        label,
        groupAreaProps,
        setIsAddAreaGroupSelected
    } = props;
    const routerHistory = useNavigate();
    const [area] = useArea(areaId);
    const { smallDevice, mediumDevice } = useWindowWidth();
    const { users: userCount, visitors: visitorCount } = usersAndVisitorsCount;
    const [, setDeleteAreaOrAreaGroup] = useDeleteAreaOrAreaGroup();
    const chosenLang = localStorage.getItem('chosenLanguage') || 'en';
    const [isHovered, setIsHovered] = useState(false);
    const isGroupedArea = groupAreaProps && groupAreaProps.groupId;
    const [areaGroup] = useAreaGroup(areaId || isGroupedArea);
    const truncatedTitle = truncateText(title, 72);
    const truncatedLabel = truncateText(label, 45);

    const areaCardImageStyles = mergeStyles({
        borderRadius: 2,
        height: 'auto',
        maxHeight: 90,
        minHeight: 48,
        width: 'auto',
        maxWidth: 220,
        padding: '4px 8px',
        border: '2px #fff solid',
        boxShadow: DefaultEffects.elevation8,
        color: theme.palette.neutralLighter,
        background: getPrimaryCardColor(),
        marginTop: -48,
        marginBottom: 16
    });

    const handleClick = (e: any) => {
        if (isGroupedArea) {
            if (groupAreaProps.groupLevel === 'L0' && groupAreaProps.groupId === areaGroup?.id) {
                localStorage.setItem('l0AGTitle', areaGroup?.name || 'LOBBY');
                localStorage.setItem('l0AGId', areaGroup?.id || '');
            }
            if (groupAreaProps.groupLevel === 'L1' && groupAreaProps.groupId === areaGroup?.id) {
                localStorage.setItem('l1AGTitle', areaGroup?.name || IvicosStrings.areaGroup);
                localStorage.setItem('l1AGId', areaGroup?.id || '');
            }
            routerHistory(`/group/${groupAreaProps.groupId}/${groupAreaProps.groupLevel}/subAreas`);
        } else {
            localStorage.setItem('areaLevel', JSON.stringify({ areaId: area.id, areaLevel: area?.level, parentAreaId: area?.parentAreaId }));
            onClick && onClick(e);
        }
    };
    return (
        <>
            <Steps
                enabled={stepsEnabled}
                onStart={() => {
                    localStorage.setItem('intro-areas', 'false');
                }}
                options={options}
                steps={steps.steps.map((step) => ({
                    ...step,
                    position: step.position === 'middle' ? 'top-middle-aligned' : step.position
                }))}
                initialStep={steps.initialStep}
                onExit={() => {
                    setStepsEnabled(false);
                }}
            />
            <Stack
                style={{
                    boxShadow: DefaultEffects.elevation16,
                    position: 'relative',
                    borderBottomRightRadius: isGroupedArea ? 24 : 0,
                    borderTopLeftRadius: area?.level && area.level !== 'L0' ? 20 : 0
                }}
                className={`${vertical ? areaCardWidthVertical : areaCardWidth} one-card`}
                aria-label="area-card"
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                <Stack
                    aria-label="area-color"
                    key="area-color"
                    horizontalAlign="end"
                    verticalAlign="center"
                    style={{ height: 32, background: getPrimaryCardColor() }}
                    horizontal
                >
                    <Stack horizontal verticalAlign="center" horizontalAlign="space-between" style={{ marginRight: 7 }}>
                        <PrimaryButton className="pressable--transparent" styles={{ root: { minWidth: 21, padding: 0 } }}>
                            {!smallDevice && !mediumDevice && onRenderActions && onRenderActions()}
                        </PrimaryButton>
                        {(identityClaims?.roles.includes('manager') || identityClaims?.roles.includes('owner')) && (
                            <PrimaryButton
                                aria-label="three-dots-button"
                                onClick={toggleIsCalloutVisible}
                                className="pressable--transparent"
                                styles={{ root: { cursor: 'pointer', minWidth: 21, padding: '0 0 0 5' } }}
                            >
                                <TooltipHost content={smallDevice ? '' : IvicosStrings.showMoreOptionsTooltip} directionalHint={DirectionalHint.topCenter}>
                                    <IC
                                        size={17}
                                        variant="custom"
                                        iconColor={color ? color && getTextColorForBackground(color) : 'white'}
                                        style={{ rotate: '90deg' }}
                                    >
                                        <MoreStrong id={buttonId} />
                                    </IC>
                                </TooltipHost>
                            </PrimaryButton>
                        )}
                    </Stack>

                    {isCalloutVisible && (
                        <Callout
                            ariaLabelledBy={labelId}
                            ariaDescribedBy={descriptionId}
                            role="alertdialog"
                            gapSpace={0}
                            target={`#${buttonId}`}
                            onDismiss={toggleIsCalloutVisible}
                            setInitialFocus
                            directionalHint={DirectionalHint.bottomCenter}
                        >
                            <Stack horizontal={false}>
                                <ActionButton
                                    onClick={() => {
                                        openPanel();
                                        if (isGroupedArea && areaGroup) {
                                            seedAreaEditPanel(areaGroup.id, areaGroup.name, areaGroup.groupTag, areaGroup.sortNumber);
                                            setIsAddAreaGroupSelected(true);
                                        } else {
                                            seedAreaEditPanel(areaId, area && area.name, area && area.tag, area?.sortNumber);
                                        }
                                        setPanelHeader(isGroupedArea ? IvicosStrings.editAreaGroupActionTitle : IvicosStrings.editAreaActionTitle);
                                    }}
                                    text={isGroupedArea ? IvicosStrings.editAreaGroupActionTitle : IvicosStrings.editAreaActionTitle}
                                    iconProps={{ iconName: 'Edit' }}
                                    aria-label="edit-area-button"
                                />
                                <ActionButton
                                    onClick={() => {
                                        setModalIsOpen(!modalIsOpen);
                                        if (isGroupedArea && areaGroup) {
                                            setDeleteAreaOrAreaGroup({
                                                id: areaGroup.id,
                                                name: areaGroup.name,
                                                parentAreaId: areaGroup?.parentAreaId,
                                                isAreaGroup: true
                                            });
                                        } else {
                                            area &&
                                                setDeleteAreaOrAreaGroup({ id: areaId, name: area.name, parentAreaId: area?.parentAreaId, isAreaGroup: false });
                                        }
                                    }}
                                    text={isGroupedArea ? IvicosStrings.deleteAreaGroupActionTitle : IvicosStrings.deleteAreaActionTitle}
                                    iconProps={{ iconName: 'Delete' }}
                                    aria-label="delete-area-button"
                                    disabled={areaGroup && areaGroup.includedAreaIds.length > 0}
                                />
                            </Stack>
                        </Callout>
                    )}
                </Stack>
                <Stack className="padding--default padding_vertical--large" onClick={handleClick}>
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                        <TooltipHost content={showTooltipForTruncatedText(label, truncatedLabel)} directionalHint={DirectionalHint.topCenter}>
                            <Stack key="area-image" verticalAlign="center" horizontalAlign="center" className={areaCardImageStyles}>
                                <Text variant="large" style={{ color: getTextColorForBackground(getPrimaryCardColor()), fontWeight: 'bold' }}>
                                    {truncatedLabel}
                                </Text>
                            </Stack>
                        </TooltipHost>

                        {(userCount > 0 || visitorCount > 0) && (
                            <Stack
                                horizontal
                                verticalAlign="center"
                                tokens={{ childrenGap: 4 }}
                                styles={{
                                    root: {
                                        height: 48,
                                        width: 48,
                                        minHeight: 48,
                                        maxHeight: 48,
                                        marginTop: -48,
                                        marginBottom: 0
                                    }
                                }}
                                aria-label="users-visitors-presence-count"
                            >
                                <TooltipHost content={getPresenceTooltip(userCount, visitorCount)} directionalHint={DirectionalHint.topCenter}>
                                    <Stack
                                        horizontal
                                        verticalAlign="center"
                                        tokens={{ childrenGap: 4 }}
                                        styles={{
                                            root: {
                                                color: color || 'white'
                                            }
                                        }}
                                    >
                                        <IC size={14} variant="custom">
                                            <PresenceIcon />
                                        </IC>
                                        {userCount > 0 && (
                                            <Text variant="small" style={{ color: '#000', marginLeft: 8, marginBottom: 8 }}>
                                                M
                                                <Text variant="large" style={{ fontWeight: 600, color: '#000', marginLeft: 2 }}>
                                                    {userCount}
                                                </Text>
                                            </Text>
                                        )}
                                        {visitorCount > 0 && (
                                            <Text variant="small" style={{ color: '#000', marginLeft: 8, marginBottom: 8 }}>
                                                {chosenLang === 'de' ? 'B' : 'V'}
                                                <Text variant="large" style={{ fontWeight: 600, color: '#000', marginLeft: 2 }}>
                                                    {visitorCount}
                                                </Text>
                                            </Text>
                                        )}
                                    </Stack>
                                </TooltipHost>
                            </Stack>
                        )}
                    </Stack>
                    <TooltipHost content={showTooltipForTruncatedText(title, truncatedTitle)} directionalHint={DirectionalHint.topCenter}>
                        <Text variant="large" style={{ fontWeight: 'bold', wordBreak: 'break-word' }}>
                            {truncatedTitle}
                        </Text>
                    </TooltipHost>
                    <Stack
                        verticalAlign="end"
                        horizontalAlign="end"
                        style={{
                            width: '100%',
                            height: 'auto',
                            marginTop: 4,
                            position: 'absolute',
                            bottom: isGroupedArea ? -1 : 5,
                            right: isGroupedArea ? -1 : 5,
                            opacity: isHovered ? 1 : 0.5
                        }}
                    >
                        {isGroupedArea ? (
                            <IC variant={'custom'} size={36}>
                                <LevelElevatorPolygon />
                            </IC>
                        ) : (
                            <IC variant={'dark'} size={36} iconColor={color}>
                                <EnterOffice />
                            </IC>
                        )}
                    </Stack>

                    {description && (
                        <Stack style={{ borderTop: '1px #e6e6e6 solid', marginTop: 16, paddingTop: 8, color: theme.palette.black }}>
                            <Text>{description}</Text>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

export default AreaCardView;
